// src/pages/churches/ChurchAdd.jsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ref, push, set } from 'firebase/database';
import { database } from '../../config/firebase';

const ChurchAdd = () => {
    const [formData, setFormData] = useState({
        nom: '',
        localisation: '',
        description: '',
        contact: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            // Vérifier que le nom est rempli
            if (!formData.nom.trim()) {
                throw new Error("Le nom de l'église est obligatoire");
            }

            // Créer une référence pour la nouvelle église
            const churchesRef = ref(database, 'churches');
            const newChurchRef = push(churchesRef);

            // Préparer les données
            const churchData = {
                nom: formData.nom.trim(),
                localisation: formData.localisation.trim(),
                description: formData.description.trim(),
                contact: formData.contact.trim(),
                createdAt: Date.now()
            };

            // Enregistrer les données
            await set(newChurchRef, churchData);

            // Initialiser les statistiques pour la nouvelle église
            const churchStatsRef = ref(database, `statistics/churches/${newChurchRef.key}`);
            await set(churchStatsRef, {
                totalMembres: 0,
                hommes: 0,
                femmes: 0,
                baptises: 0
            });

            // Rediriger vers la liste des églises
            navigate('/churches');
        } catch (error) {
            console.error("Erreur lors de l'ajout de l'église:", error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="mb-8">
                <div className="flex items-center">
                    <Link to="/churches" className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500">
                        <svg className="-ml-1 mr-1 h-5 w-5 text-indigo-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Retour à la liste
                    </Link>
                </div>
                <h1 className="mt-4 text-2xl font-bold text-gray-900">Ajouter une nouvelle église</h1>
                <p className="mt-2 text-sm text-gray-600">
                    Remplissez le formulaire ci-dessous pour ajouter une nouvelle église au système.
                </p>
            </div>

            {error && (
                <div className="mb-4 bg-red-50 border-l-4 border-red-500 text-red-700 p-4" role="alert">
                    <p>{error}</p>
                </div>
            )}

            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <form onSubmit={handleSubmit} className="p-6">
                    <div className="space-y-6">
                        <div>
                            <label htmlFor="nom" className="block text-sm font-medium text-gray-700">
                                Nom de l'église <span className="text-red-500">*</span>
                            </label>
                            <div className="mt-1">
                                <input
                                    type="text"
                                    id="nom"
                                    name="nom"
                                    value={formData.nom}
                                    onChange={handleChange}
                                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="localisation" className="block text-sm font-medium text-gray-700">
                                Localisation
                            </label>
                            <div className="mt-1">
                                <input
                                    type="text"
                                    id="localisation"
                                    name="localisation"
                                    value={formData.localisation}
                                    onChange={handleChange}
                                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                />
                            </div>
                            <p className="mt-1 text-xs text-gray-500">Exemple: "Cotonou, Bénin" ou "Quartier Agla, Cotonou"</p>
                        </div>

                        <div>
                            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                                Description
                            </label>
                            <div className="mt-1">
                <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                ></textarea>
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                                Brève description de l'église (facultatif)
                            </p>
                        </div>

                        <div>
                            <label htmlFor="contact" className="block text-sm font-medium text-gray-700">
                                Contact
                            </label>
                            <div className="mt-1">
                                <input
                                    type="text"
                                    id="contact"
                                    name="contact"
                                    value={formData.contact}
                                    onChange={handleChange}
                                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                />
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                                Numéro de téléphone ou email de contact (facultatif)
                            </p>
                        </div>

                        <div className="pt-5 flex justify-end space-x-3">
                            <Link
                                to="/churches"
                                className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                Annuler
                            </Link>
                            <button
                                type="submit"
                                disabled={loading}
                                className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                            >
                                {loading ? (
                                    <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Enregistrement...
                  </span>
                                ) : 'Enregistrer'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ChurchAdd;
